import {
  MedplumClient,
  getReferenceString,
  createReference,
} from '@medplum/core';
import {
  Attachment,
  CodeableConcept,
  DiagnosticReport,
  Encounter,
  Patient,
} from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { showNotification } from '@mantine/notifications';
import {
  ARKON,
  REPORT_MODALITY_EXTENSION_URL,
  SYSTEMS,
} from '@consts/systems.consts';
import {
  ALL_REPORT_CATEGORY_OPTIONS,
  REPORT_CATEGORY_OPTIONS,
  REPORT_MODALITY_OPTIONS,
  REPORT_RESULT_OPTIONS,
  ReportCategoryValue,
  ReportResultValue,
} from './report-files.consts';
import {
  FileDownloadProps,
  FileValidationOptions,
  FileValidationResult,
  ReportFormState,
  ReportSaveParams,
  FileUploadResult,
} from './report-files.types';

//------------------------------------------------------------------------------------------------
// Constants
//------------------------------------------------------------------------------------------------

/** Maximum file size allowed for uploads (10MB) */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Valid PDF MIME type */
export const PDF_MIME_TYPE = 'application/pdf';

//------------------------------------------------------------------------------------------------
// File Validation
//------------------------------------------------------------------------------------------------
export const validateFileType = (
  file: File | null,
  allowedTypes: string[] = [PDF_MIME_TYPE]
): boolean => {
  if (!file) {
    return true;
  }
  return allowedTypes.includes(file.type);
};

export const validateFileSize = (
  file: File | null,
  maxSize: number = MAX_FILE_SIZE
): boolean => {
  if (!file) {
    return true;
  }
  return file.size <= maxSize;
};

export const validateFile = (
  file: File | null,
  options: FileValidationOptions = {}
): FileValidationResult => {
  const { maxSize = MAX_FILE_SIZE, allowedMimeTypes = [PDF_MIME_TYPE] } =
    options;

  const isPdf = validateFileType(file, allowedMimeTypes);
  const isSizeValid = validateFileSize(file, maxSize);
  const isValid = isPdf && isSizeValid;

  return {
    isValid,
    isPdf,
    isSizeValid,
    size: file?.size || 0,
    maxSize,
  };
};

export const validateFileForUpload = (
  file: File | null,
  t?: TFunction<'files-tab'>,
  maxSize: number = MAX_FILE_SIZE
): FileValidationResult => {
  return validateFile(file, {
    maxSize,
    allowedMimeTypes: [PDF_MIME_TYPE],
  });
};

export const isValidDate = (date: string): boolean => {
  if (!date || date.trim() === '') {
    return false;
  }

  const dateObj = new Date(date);
  const now = new Date();

  return !isNaN(dateObj.getTime()) && dateObj <= now;
};

//------------------------------------------------------------------------------------------------
// File Operations
//------------------------------------------------------------------------------------------------

export const downloadFile = async ({
  medplum,
  url,
  fileName,
  t,
}: FileDownloadProps): Promise<void> => {
  try {
    const pdfBlob = await medplum.download(url);
    const blobUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
  } catch (error) {
    console.error('Error downloading file:', error);
    showFileDownloadError(t, error);
  }
};

//------------------------------------------------------------------------------------------------
// File Upload and Document Reference Operations
//------------------------------------------------------------------------------------------------

export const handleFileUpload = async (
  file: File,
  medplum: MedplumClient
): Promise<FileUploadResult> => {
  const binary = await medplum.createBinary({
    data: file,
    filename: file.name,
    contentType: 'application/pdf',
  });

  return {
    title: file.name,
    url: getReferenceString(binary),
    contentType: 'application/pdf',
  };
};

//------------------------------------------------------------------------------------------------
// Notifications
//------------------------------------------------------------------------------------------------
export const showFileDownloadError = (
  t: TFunction<'files-tab'>,
  error: unknown
): void => {
  console.error('File download error:', error);
  showNotification({
    color: 'red',
    title: t('error', { ns: 'common' }),
    message: t('notifications.error.fileUploadFailed'),
  });
};

export const REPORT_CATEGORY_TO_DIAGNOSTIC_CATEGORY: Record<
  ReportCategoryValue,
  { system: string; code: string; display: string }
> = {
  genetic: {
    system: ARKON.CS.REPORTS.CATEGORY.BASE,
    code: ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC,
    display: 'Genetic',
  },
  investigation: {
    system: ARKON.CS.REPORTS.CATEGORY.BASE,
    code: ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION,
    display: 'Investigation',
  },
  biopsy: {
    system: ARKON.CS.REPORTS.CATEGORY.BASE,
    code: ARKON.CS.REPORTS.CATEGORY.CODES.BIOPSY,
    display: 'Biopsy',
  },
  'prenatal-check': {
    system: ARKON.CS.REPORTS.CATEGORY.BASE,
    code: ARKON.CS.REPORTS.CATEGORY.CODES.PRENATAL_CHECK,
    display: 'Prenatal Check',
  },
  'pre-implantation-check': {
    system: ARKON.CS.REPORTS.CATEGORY.BASE,
    code: ARKON.CS.REPORTS.CATEGORY.CODES.PRE_IMPLANTATION_CHECK,
    display: 'Pre-Implantation Check',
  },
};

const DEFAULT_CATEGORY = REPORT_CATEGORY_OPTIONS[0].value;

export const getReportCategoryFromResource = (
  report: DiagnosticReport
): ReportCategoryValue => {
  const categoryCoding = report.category?.[0]?.coding?.[0];
  const derivedCategory =
    (Object.entries(REPORT_CATEGORY_TO_DIAGNOSTIC_CATEGORY).find(
      ([, coding]) =>
        coding.code === categoryCoding?.code &&
        coding.system === categoryCoding?.system
    )?.[0] as ReportCategoryValue) ?? DEFAULT_CATEGORY;
  return derivedCategory;
};

export const getReportCategoryLabel = (
  category: ReportCategoryValue
): string => {
  return (
    ALL_REPORT_CATEGORY_OPTIONS.find((option) => option.value === category)
      ?.label || category
  );
};

export const getReportResultValueFromCode = (
  code?: string
): ReportResultValue | null => {
  return (
    REPORT_RESULT_OPTIONS.find((option) => option.coding.code === code)
      ?.value || null
  );
};

export const getReportResultLabel = (
  result: ReportResultValue | null
): string | null => {
  if (!result) {
    return null;
  }
  return (
    REPORT_RESULT_OPTIONS.find((option) => option.value === result)?.label ||
    null
  );
};

export const createInitialReportFormState = (
  encounter?: Encounter | null
): ReportFormState => ({
  category: DEFAULT_CATEGORY,
  geneticTestCode: null,
  geneticTestDisplay: '',
  investigationType: null,
  investigationModality: null,
  biopsyType: '',
  date: new Date().toISOString().split('T')[0],
  result: null,
  comments: '',
  file: null,
  encounterId: encounter?.id || null,
});

export const mapReportToFormState = (
  report: DiagnosticReport
): ReportFormState => {
  const derivedCategory = getReportCategoryFromResource(report);

  const geneticCoding =
    report.code?.coding?.find(
      (coding) =>
        coding.system === SYSTEMS.SNOMED || coding.system?.includes('arkon')
    ) || report.code?.coding?.[0];

  const investigationCoding = report.code?.coding?.find(
    (coding) => coding.system === ARKON.CS.REPORTS.INVESTIGATION_TYPE.BASE
  );

  const investigationCodeableConcept: CodeableConcept | null =
    derivedCategory === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION &&
    report.code
      ? {
          coding: investigationCoding
            ? [{ ...investigationCoding }]
            : report.code.coding || [],
          text: report.code.text || investigationCoding?.display || undefined,
        }
      : null;

  const biopsyText =
    derivedCategory === ARKON.CS.REPORTS.CATEGORY.CODES.BIOPSY
      ? report.code?.text || investigationCoding?.display || ''
      : '';

  const resultValue = getReportResultValueFromCode(
    report.conclusionCode?.[0]?.coding?.[0]?.code
  );

  return {
    category: derivedCategory,
    geneticTestCode:
      derivedCategory === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC
        ? geneticCoding?.code || null
        : null,
    geneticTestDisplay:
      derivedCategory === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC
        ? geneticCoding?.display || ''
        : '',
    investigationType: investigationCodeableConcept,
    investigationModality:
      derivedCategory === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION
        ? report.extension?.find(
            (ext) => ext.url === REPORT_MODALITY_EXTENSION_URL
          )?.valueCode || null
        : null,
    biopsyType: biopsyText,
    date: report.effectiveDateTime?.slice(0, 10) || '',
    result: (resultValue as ReportResultValue | undefined) || null,
    comments: report.conclusion || '',
    file: null,
    encounterId: report.encounter?.reference?.replace('Encounter/', '') || null,
  };
};

export const buildReportCode = (
  formState: ReportFormState
): CodeableConcept => {
  if (formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC) {
    return {
      coding: [
        {
          system: ARKON.CS.GENETIC_TESTS.BASE,
          code: formState.geneticTestCode || undefined,
          display: formState.geneticTestDisplay || undefined,
        },
      ],
      text: formState.geneticTestDisplay || undefined,
    };
  }

  if (
    formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION &&
    formState.investigationType
  ) {
    return formState.investigationType;
  }

  return {
    text: formState.biopsyType || undefined,
  };
};

export const buildConclusionCode = (result: ReportResultValue | null) => {
  if (!result) {
    return [
      {
        coding: [REPORT_RESULT_OPTIONS[0].coding],
      },
    ];
  }

  const coding = REPORT_RESULT_OPTIONS.find(
    (option) => option.value === result
  )?.coding;

  if (!coding) {
    return undefined;
  }

  return [
    {
      coding: [coding],
    },
  ];
};

export const getModalityOptions = (
  investigationType: CodeableConcept | null
) => {
  if (!investigationType) {
    return null;
  }

  const display =
    investigationType.coding?.[0]?.display || investigationType.text;
  const code = investigationType.coding?.[0]?.code;

  if (!display && !code) {
    return null;
  }

  if (
    display === 'Απεικόνιση εγκεφάλου' ||
    display === 'Απεικόνιση κοιλιακής χώρας - ΝΟΚ'
  ) {
    return REPORT_MODALITY_OPTIONS.BRAIN_OR_ABDOMEN;
  }

  if (display === 'Απεικόνιση θώρακα - πνεύμονα') {
    return REPORT_MODALITY_OPTIONS.THORAX;
  }

  return null;
};

export const buildPresentedForm = (
  attachment: Attachment | undefined
): Attachment[] | undefined => {
  if (!attachment) {
    return undefined;
  }
  return [attachment];
};

export const buildEncounterReference = (encounterId?: string | null) => {
  if (!encounterId) {
    return undefined;
  }
  return {
    reference: `Encounter/${encounterId}`,
  };
};

export const hasCategorySpecificData = (
  formState: ReportFormState
): boolean => {
  if (formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC) {
    return Boolean(formState.geneticTestCode);
  }

  if (formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION) {
    return Boolean(
      formState.investigationType?.coding?.[0]?.code ||
        formState.investigationType?.text
    );
  }

  return Boolean(formState.biopsyType?.trim().length);
};

export const buildDiagnosticReportPayload = ({
  formState,
  patient,
  encounterId,
  attachment,
}: {
  formState: ReportFormState;
  patient: Patient;
  encounterId?: string | null;
  attachment?: Attachment;
}) => {
  const coding = REPORT_CATEGORY_TO_DIAGNOSTIC_CATEGORY[formState.category];
  const modalityExtension =
    formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION &&
    formState.investigationModality
      ? [
          {
            url: REPORT_MODALITY_EXTENSION_URL,
            valueCode: formState.investigationModality,
          },
        ]
      : [];
  const extensions = modalityExtension.length ? modalityExtension : undefined;

  return {
    resourceType: 'DiagnosticReport' as const,
    status: 'final' as const,
    category: [
      {
        coding: [coding],
        text: coding.display,
      },
    ],
    code: buildReportCode(formState),
    subject: createReference(patient),
    encounter: buildEncounterReference(encounterId),
    effectiveDateTime: formState.date
      ? new Date(formState.date + 'T00:00:00Z').toISOString()
      : undefined,
    conclusion: formState.comments || undefined,
    conclusionCode: buildConclusionCode(formState.result),
    extension: extensions,
    presentedForm: buildPresentedForm(attachment),
  };
};

//------------------------------------------------------------------------------------------------
// Report Utilities

interface FetchDiagnosticReportsParams {
  medplum: MedplumClient;
  patientId: string;
  encounterId?: string | null;
}

const mapUploadResultToAttachment = (
  uploadResult: Awaited<ReturnType<typeof handleFileUpload>>
): Attachment => ({
  title: uploadResult.title,
  url: uploadResult.url,
  contentType: uploadResult.contentType,
});

export const fetchDiagnosticReportsForPatient = async ({
  medplum,
  patientId,
  encounterId,
}: FetchDiagnosticReportsParams): Promise<DiagnosticReport[]> => {
  if (!patientId) {
    return [];
  }

  const encounterFilter = encounterId
    ? `&encounter=Encounter/${encounterId}`
    : '';
  const searchParams = `subject=Patient/${patientId}${encounterFilter}&_sort=-date`;

  return medplum.searchResources('DiagnosticReport', searchParams);
};

const resolveAttachment = async (
  medplum: MedplumClient,
  formState: ReportFormState,
  existingReport?: DiagnosticReport | null
): Promise<Attachment | undefined> => {
  if (formState.file) {
    const uploadResult = await handleFileUpload(formState.file, medplum);
    return mapUploadResultToAttachment(uploadResult);
  }

  return existingReport?.presentedForm?.[0];
};

export const saveDiagnosticReport = async ({
  medplum,
  patient,
  encounterId,
  formState,
  existingReport,
}: ReportSaveParams): Promise<DiagnosticReport> => {
  const attachment = await resolveAttachment(
    medplum,
    formState,
    existingReport
  );
  const payload = buildDiagnosticReportPayload({
    formState,
    patient,
    encounterId: formState.encounterId ?? encounterId,
    attachment,
  });

  if (existingReport?.id) {
    return medplum.updateResource({
      ...existingReport,
      ...payload,
      id: existingReport.id,
      resourceType: 'DiagnosticReport',
    });
  }

  return medplum.createResource(payload);
};

export const deleteDiagnosticReport = async (
  medplum: MedplumClient,
  report: DiagnosticReport
): Promise<void> => {
  if (!report.id) {
    return;
  }
  await medplum.deleteResource('DiagnosticReport', report.id);
};
